import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useContext, useEffect, useState, useCallback } from 'react';
import '../template.module.css';
import { ReportTemplatesContext } from '..';
import { FixedSizeList } from 'react-window';

export default function AddBlockModal() {
  const {
    setAddBlockModal,
    Sections,
    setSections,
    addBlockModal,
    fetchLayerUrlsAndTitles,
    fetchToken,
    fetchFields,
    getAccessToken,
    updateSections,
  } = useContext(ReportTemplatesContext);
  const [searchText, setSearchText] = useState('');
  const [groupLoader, setGroupLoader] = useState(true);
  const [blockLoader, setBlockLoader] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // will be one of the layer names

  const handleAddBlock = useCallback(
    (block, sectionTitle) => {
      const updatedSections = Sections.map(section => {
        if (section.key === sectionTitle) {
          const activeBlocksCount = section.blocks.filter(
            secBlock => secBlock.status,
          ).length;
          if (activeBlocksCount >= 9) {
            alert('Cannot add more than 9 blocks in a section');
            return section;
          }
          section.blocks = section.blocks.map(secBlock => {
            if (secBlock.title === block.title) {
              return { ...secBlock, status: true };
            }

            return secBlock;
          });
        }
        return section;
      });

      setSections(updatedSections);
    },
    [Sections, setSections],
  );

  // Data Block Searching
  const blocksToShow = Sections.flatMap(section =>
    section.key === addBlockModal.callingSection
      ? section.blocks
          .map(block => {
            if (block.status) return null;
            const searchWords = searchText.toLowerCase().split(' ');
            const title = block.title.toLowerCase();
            const score = searchWords.reduce((acc, word) => {
              return acc + (title.includes(word) ? 1 : 0);
            }, 0);
            return { ...block, score };
          })
          .filter(block => block && block.score > 0)
          .sort((a, b) => b.score - a.score)
      : [],
  );

  async function getGroupsForMapServer(mapserverURL) {
    try {
      console.log('Getting Groups for Map server...');
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/map/groups`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            // Use JSON.stringify here
            mapserverURL: mapserverURL,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Couldn't fetch the layers");
      }

      const data = await response.json();
      console.log({ data });

      if (!data.layers) {
        throw new Error('Layers data is missing from the response');
      }

      console.log({ layers: data.layers });

      return data.layers;
    } catch (error) {
      console.error(error);
      throw error; // Re-throw the error to handle it in the calling function
    }
  }

  async function localGroupsFetcherRoutine() {
    try {
      const callingSectionTemp = Sections.find(
        section => section.key === addBlockModal.callingSection,
      );
      const token = await fetchToken();

      const layerUrlsAndTitles = await fetchLayerUrlsAndTitles(
        callingSectionTemp.map.webmapID,
        token,
      );

      if (!layerUrlsAndTitles) {
        console.log('layerUrlsAndTitles is empty');
        return;
      }

      console.log({ layerUrlsAndTitles });
      let mapServerURL = null;
      layerUrlsAndTitles.forEach(layer => {
        if (layer?.url.includes('/MapServer')) {
          const match = layer.url.match(/.*\/MapServer\//);
          if (match) {
            mapServerURL = match[0];
          }
        }
      });

      if (mapServerURL) {
        console.log(mapServerURL);
        const groupNamesAndIDs = await getGroupsForMapServer(mapServerURL);

        const groupUrlsAndNames = groupNamesAndIDs.map(group => {
          return {
            url: `${mapServerURL}${group.id}`,
            title: group.name,
          };
        });

        console.log({ groupUrlsAndNames });
        setGroups(groupUrlsAndNames);
      } else {
        const layers = layerUrlsAndTitles.map(layer => layer.url);
        const newBlocks = await fetchFields(layers, token);
        if (!newBlocks) {
          throw new Error('newBlocks is empty');
        }
        console.log({ newBlocks });
        updateSections(addBlockModal.callingSection, newBlocks);
        setSelectedGroup('None');
        setBlockLoader(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGroupLoader(false);
    }
  }

  async function localBlocksFetcherRoutine() {
    try {
      setBlockLoader(true);
      const token = await fetchToken();

      const layerUrls = [];
      layerUrls.push(selectedGroup.url);
      const newBlocks = await fetchFields(layerUrls, token);
      if (!newBlocks) {
        throw new Error('newBlocks is empty');
      }
      console.log({ newBlocks });
      updateSections(addBlockModal.callingSection, newBlocks);
    } catch (error) {
      console.error(error);
    } finally {
      setBlockLoader(false);
    }
  }

  useEffect(() => {
    if (addBlockModal.status === true) localGroupsFetcherRoutine();
  }, []);

  useEffect(() => {
    if (!selectedGroup || selectedGroup === 'None') return;
    setBlockLoader(true);
    localBlocksFetcherRoutine();
  }, [selectedGroup]);

  return (
    <Box
      sx={{
        zIndex: 3,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* Actual Modal */}
      <Box
        sx={{
          width: '40.5rem',
          height: '35.25rem',
          backgroundColor: '#FFFFFF',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.85rem',
        }}
      >
        {/* Heading + Close Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F9F9F9',
            padding: '1.5rem',
            borderTopLeftRadius: '1rem',
            borderTopRightRadius: '1rem',
          }}
        >
          {/* Heading */}
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 700,
              color: '#004852',
            }}
          >
            Data Block Name
          </Typography>
          {/* Close Button */}
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              setAddBlockModal({ status: false, callingSection: null });
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='lucide lucide-x'
            >
              <path d='M18 6 6 18' />
              <path d='m6 6 12 12' />
            </svg>
          </Box>
        </Box>

        {selectedGroup === null ? (
          groupLoader === true ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '5rem',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                overflowY: 'auto',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#004852',
                  marginLeft: '1rem',
                }}
              >
                Pick a group to fetch its blocks
              </Typography>
              <Box>
                {groups.map(group => {
                  return (
                    <Box
                      key={group.title}
                      sx={{
                        paddingY: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          marginLeft: '1rem',
                          fontFamily: 'Lato',
                          fontSize: '1rem',
                          fontWeight: 700,
                          width: '22rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {group.title}
                      </Typography>
                      <Button
                        sx={{
                          backgroundColor: '#4FA490',
                          height: '3.5rem',
                          width: '14rem', // Fixed width
                          color: '#FFFFFF',
                          padding: '0.5rem 1.25rem',
                          borderRadius: '0.75rem',
                          '&:hover': {
                            backgroundColor: '#4FA490',
                          },
                          marginRight: '1rem',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => {
                          console.log({ group });
                          setSelectedGroup(group);
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Lato',
                            fontSize: '1rem',
                            fontWeight: 700,
                          }}
                        >
                          Select Group
                        </Typography>
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )
        ) : (
          <>
            {/* Search Bar */}
            <Box sx={{ marginX: '1.5rem' }}>
              <input
                type='text'
                id='blockName'
                name='blockName'
                placeholder='Search for Data Blocks'
                style={{
                  boxSizing: 'border-box',
                  height: '3rem',
                  width: '100%',
                  padding: '1rem',
                  border: '1px solid #E2E8F0',
                  borderRadius: '0.75rem',
                }}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </Box>

            {/* Virtualized Block List */}
            {blockLoader === true ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '5rem',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <FixedSizeList
                height={400}
                itemCount={blocksToShow.length}
                itemSize={80}
                width={'100%'}
                style={{
                  display: 'flex',
                }}
              >
                {({ index, style }) => {
                  const block = blocksToShow[index];
                  return (
                    <Box
                      key={block.title}
                      sx={{
                        ...style,
                        paddingY: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        maxHeight: '4.5rem', // Ensure the height remains constant
                      }}
                    >
                      <Typography
                        sx={{
                          marginLeft: '1rem',
                          fontFamily: 'Lato',
                          fontSize: '1rem',
                          fontWeight: 700,
                          width: '22rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal', // Allow text to wrap
                        }}
                      >
                        {block.title}
                      </Typography>
                      <Button
                        sx={{
                          backgroundColor: '#4FA490',
                          height: '3.5rem',
                          width: '14rem', // Fixed width
                          color: '#FFFFFF',
                          padding: '0.5rem 1.25rem',
                          borderRadius: '0.75rem',
                          '&:hover': {
                            backgroundColor: '#4FA490',
                          },
                          marginRight: '1rem',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() =>
                          handleAddBlock(block, addBlockModal.callingSection)
                        }
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Lato',
                            fontSize: '1rem',
                            fontWeight: 700,
                          }}
                        >
                          Add Data Block
                        </Typography>
                      </Button>
                    </Box>
                  );
                }}
              </FixedSizeList>
            )}
          </>
        )}
        {/* Render this only if selected is not null */}
      </Box>
    </Box>
  );
}
